import { GoodgameBitmapEngine } from "./GoodgameBitmapEngine";
import { ClipCacheController } from "./cache/ClipCacheController";
import { ICacheController } from "./cache/ICacheController";
import { IClipSource } from "./clipsource/IClipSource";
import { IExternalClipSource } from "./clipsource/IExternalClipSource";
import { ContinueLoadingTexturePackerClipSource } from "./clipsource/concreteSources/ContinueLoadingTexturePackerClipSource";
import { DisplayObjectClipSource } from "./clipsource/concreteSources/DisplayObjectClipSource";
import { ExternalDisplayObjectClipSource } from "./clipsource/concreteSources/ExternalDisplayObjectClipSource";
import { ExternalTexturePackerClipSource } from "./clipsource/concreteSources/ExternalTexturePackerClipSource";
import { ClipColor } from "./components/ClipColor";

/**
 * converts a range of input types to a valid source for GoodgameBitmapClip
 */
export class ClipSourceFactory {
    private _debugMode: boolean = false;

    private _cacheController: ICacheController = new ClipCacheController();

    /** cache controller for this framework. */
    public get cacheController(): ICacheController {
        return this._cacheController;
    }

    /** cache controller for this framework. */
    public set cacheController(value: ICacheController) {
        if (this._cacheController) {
            this._cacheController.dispose();
        }
        this._cacheController = value;
    }

    constructor(debugMode: boolean = false, customCacheController: ICacheController = null) {
        this._debugMode = debugMode;

        if (customCacheController) {
            this.cacheController = customCacheController;
        }
    }

    /**
     * returns a ClipSourceObject from cache or creates a new one
     *
     * @param clipClass (must be a DisplayObject)
     * @param clipSourceClass custom clipsource class
     *
     */
    public getClipSource(clipClass: Object, clipSourceClass: any = null, clipColor: ClipColor[] = null, hasSubLayer: boolean = false): IClipSource {
        // TODO: @HTML5 - changed getQualifiedClassName to toString()
        let cacheID: string = clipClass.toString();
        let source: IClipSource = GoodgameBitmapEngine.cacheController.getCache(cacheID, clipColor);
        if (!source) {
            if (!clipSourceClass) {
                source = new DisplayObjectClipSource();
            }
            else {
                source = new clipSourceClass();
            }

            source.convertClass(clipClass, clipColor, this.getNextRandomDebugColor(), hasSubLayer);
            GoodgameBitmapEngine.cacheController.addToCache(cacheID, source);
        }
        return source;
    }

    /**
     * returns a ClipSourceObject from cache or creates a new one from loaded swf
     *
     * @param clipClassName as String
     * @param swfSourcePath path of the external .swf file
     * @param clipSourceClass custom clipsource class
     * @param clipSourceClassConstructorArgs constructor parameters for custom clipsource class
     *
     */
    public getExternalClipSource(clipClassName: string, swfSourcePath: string, clipColor: ClipColor[] = null, clipSourceClass: any = null, clipSourceClassConstructorArgs: Object = null, hasSubLayer: boolean = false, priority: number = 1): IExternalClipSource {
        let cacheID: string = clipClassName;
        let source: IExternalClipSource = (<IExternalClipSource>GoodgameBitmapEngine.cacheController.getCache(cacheID, clipColor));

        if (!source) {
            if (!clipSourceClass) {
                source = new ExternalDisplayObjectClipSource();
                source.initExternalClipSource(clipClassName, swfSourcePath, clipColor, this.getNextRandomDebugColor(), hasSubLayer, priority);
            }
            else {
                if (clipSourceClassConstructorArgs) {
                    source = new clipSourceClass(clipSourceClassConstructorArgs);
                }
                else {
                    source = new clipSourceClass();
                }
                source.initExternalClipSource(clipClassName, swfSourcePath, clipColor, this.getNextRandomDebugColor(), hasSubLayer, priority);
            }
            GoodgameBitmapEngine.cacheController.addToCache(cacheID, source);
        }
        return source;
    }


    public getExternalTexturePackerClipSource(objectClipClassName: string, assetFileURL: string, prio: number = 1): IExternalClipSource {
        let cacheID: string = objectClipClassName;
        // TODO: @HTML5 - pass a real array instead of []
        let source: IExternalClipSource = (<IExternalClipSource>GoodgameBitmapEngine.cacheController.getCache("externaltp_" + cacheID, []));
        if (!source) {
            let externalTexturePackerClipSource: ExternalTexturePackerClipSource = new ExternalTexturePackerClipSource();
            externalTexturePackerClipSource.initExternalClipSource(objectClipClassName, assetFileURL, null, 0, false, prio);
            source = externalTexturePackerClipSource;
            GoodgameBitmapEngine.cacheController.addToCache("externaltp_" + cacheID, source);
        }
        return source;
    }

    public getContinuesLoadedTexturePackerClipSource(objectClipClassName: string, assetFileURL: string, prio: number = 1): IExternalClipSource {
        let cacheID: string = objectClipClassName;
        // TODO: @HTML5 - pass a real array instead of []
        let source: IExternalClipSource = (<IExternalClipSource>GoodgameBitmapEngine.cacheController.getCache("externaltp_" + cacheID, []));
        if (!source) {
            let externalTexturePackerClipSource: ContinueLoadingTexturePackerClipSource = new ContinueLoadingTexturePackerClipSource();
            externalTexturePackerClipSource.initExternalClipSource(objectClipClassName, assetFileURL, null, 0, false, prio);
            source = externalTexturePackerClipSource;
            GoodgameBitmapEngine.cacheController.addToCache("externaltp_" + cacheID, source);
        }
        return source;
    }

    /**
     * returns a random color
     */
    private getNextRandomDebugColor(): number {
        if (this._debugMode) {
            return Number.MAX_VALUE * Math.random() + 1;
        }
        else {
            return 0;
        }
    }
}
